/**
 * KpiTile — a "stat card" rendered at the top of analytical chat answers.
 *
 * Visual reference: screenshots 1 and 10 from the original dynamic-report
 * thread — three-up cards with a small label, a big number, and a sub-line.
 * The card carries a tone (positive/negative/neutral) that picks the
 * matching valence palette from `tokens.css`.
 *
 * Empty/error states are intentionally a property of *this* component so
 * the model never has to invent its own missing-number markup. Pass
 * `value === ''` (or `null`/`undefined`) and the tile renders an "n/a"
 * dash in muted neutral tone, regardless of what `tone` was requested.
 */
import type { ReactNode } from 'react';
import { coerceTone, type Tone } from './tone';

export interface KpiTileProps {
  /** Short uppercase label above the number, e.g. "NET IMPACT". */
  label: string;
  /**
   * Pre-formatted display value (the model emits already-formatted strings
   * via the `format-number` helper). Empty / null / undefined renders the
   * neutral "n/a" placeholder.
   */
  value: string | number | null | undefined;
  /**
   * Optional explanatory line under the value (e.g. "Top quartile within
   * European utilities"). Renders inline citations / followups verbatim
   * because it accepts ReactNode, so the markdown pipeline above can
   * still slot in a `<cite id="…"/>` pill.
   */
  sub?: ReactNode;
  /** Valence colouring. Defaults to neutral. */
  tone?: Tone;
}

export function KpiTile({ label, value, sub, tone = 'neutral' }: KpiTileProps) {
  const safeTone: Tone = coerceTone(tone);
  const isEmpty = value === null || value === undefined || value === '';
  const displayValue = isEmpty ? '—' : typeof value === 'number' ? String(value) : value;
  const effectiveTone: Tone = isEmpty ? 'neutral' : safeTone;

  return (
    <div className={`kpi-tile kpi-tile--${effectiveTone}`} role="group" aria-label={label}>
      <div className="kpi-tile-label">{label}</div>
      <div className={`kpi-tile-value${isEmpty ? ' kpi-tile-value--empty' : ''}`}>{displayValue}</div>
      {sub !== undefined && sub !== null && sub !== '' && <div className="kpi-tile-sub">{sub}</div>}
    </div>
  );
}

/**
 * Convenience wrapper: a 1–4 column row of tiles. Used by the
 * `present_headline_kpis` tool UI; also exposed for the markdown directive
 * in case we add a `:::kpis` block later.
 */
export function KpiTileRow({ tiles }: { tiles: KpiTileProps[] }) {
  // Cap at 4 — beyond that the row visually breaks down. We trim instead of
  // throw so a misbehaving model can't break the entire chat answer.
  const capped = tiles.slice(0, 4);
  if (capped.length === 0) return null;
  return (
    <div className={`kpi-tile-row kpi-tile-row--${capped.length}`}>
      {capped.map((tile, i) => (
        <KpiTile key={i} {...tile} />
      ))}
    </div>
  );
}
